import * as React from 'react';
import styled from 'styled-components';
import {
  COLORS,
  ComponentTextStyle,
  ComponentXXS,
  LinearProgress,
  LinearProgressType,
  LinearProgressVariant,
  Size
} from '../../index';
import {IconButton} from '../../Button';

export interface ActionItem {
  icon: React.ReactNode;
  onClick: () => void;
}

export interface CardBottomSectionProps {
  progressLevel?: number;
  progressMax?: number;
  progressType?: LinearProgressType;

  noteLeft?: string;
  noteLeftIcon?: React.ReactNode;
  noteRight?: string;
  noteRightIcon?: React.ReactNode;

  authorName?: string;
  actions?: ActionItem[];
  logo?: React.ReactNode;
  disabled?: boolean;
}

export const CardBottomSectionProgressStyles = styled.div`
  padding: 8px 0;
`;

export const CardBottomSectionNotesStyles = styled.div`
  height: 48px;
  position: relative;
  color: ${COLORS.neutral_500};
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const CardBottomSectionAuthorStyles = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 48px;

  div:first-child {
    flex-grow: 2;
  }

  svg, img {
    width: 80px;
    flex-grow: 0;
    flex-shrink: 0;
  }

  ${props => props.disabled ? `
    color: ${COLORS.neutral_300};
    svg, img {
      filter: grayscale(100%);
    }
    
  ` : ''}
`;

export const CardBottomSectionDivider = styled.div`
  border-top: 1px;
  border-top-color: ${COLORS.neutral_100};
  border-top-style: solid;
  width: 100%;
`;

export const CardBottomSectionNoteLeftStyles = styled.div`
  position: absolute;
  left: 0px;
  display: flex;
  flex-direction: row;
  align-items: center;

  svg {
    margin-right: 4px;
    flex-grow: 0;
    flex-shrink: 0;
  }
`;

export const CardBottomSectionNoteRightStyles = styled.div`
  position: absolute;
  right: 0px;
  display: flex;
  flex-direction: row;
  align-items: center;

  svg {
    margin-right: 4px;
    flex-grow: 0;
    flex-shrink: 0;
  }
`;

export const CardBottomSectionButtonRowContainerStyles = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  border-top: 1px;
  border-top-color: ${COLORS.neutral_100};
  border-top-style: solid;

  button:last-child {
    margin: 8px 0px 0px 0px;
  }

  button:not(:last-child) {
    margin: 8px 0px 0px 0px;
  }

  svg {
    color: ${COLORS.neutral_600};
  }
`;

export const CardBottomSectionContainer = styled.div`
  padding: 0px 16px 8px 16px;
  width: calc(100% - 32px);
`;

const CardBottomSection = React.forwardRef(({
                                              progressLevel,
                                              progressMax,
                                              progressType = LinearProgressType.Line,
                                              noteLeft,
                                              noteLeftIcon,
                                              noteRight,
                                              noteRightIcon,
                                              authorName,
                                              logo,
                                              actions,
                                              disabled
                                            }: CardBottomSectionProps,
                                            ref: React.Ref<React.RefObject<HTMLButtonElement>[]>) => {

  const [elRefs, setElRefs] = React.useState<React.RefObject<HTMLButtonElement>[]>([]);

  const length = actions?.length || 0;

  React.useEffect(() => {
    setElRefs(Array(length || 0).fill(null).map(() => React.createRef<HTMLButtonElement>()));
  }, [length]);

  React.useImperativeHandle(ref, () => elRefs, [elRefs]);

  const haveAtLeastSomething = progressLevel != undefined || noteLeft || noteLeftIcon || noteRight || noteRightIcon || authorName;

  return (
    <CardBottomSectionContainer data-testid={'card-bottomSection'}>
      {haveAtLeastSomething && <CardBottomSectionDivider/>}
      {progressLevel != undefined && <CardBottomSectionProgressStyles>
        <LinearProgress size={Size.Small}
                        type={progressType}
                        variant={LinearProgressVariant.Normal}
                        value={progressLevel}
                        max={progressMax ?? progressLevel}/>
      </CardBottomSectionProgressStyles>}

      {(noteLeft || noteLeftIcon || noteRight || noteRightIcon) && <CardBottomSectionNotesStyles>
        {(noteLeft || noteLeftIcon) && (
          <CardBottomSectionNoteLeftStyles>
            {noteLeftIcon}
            <ComponentXXS color={COLORS.neutral_500} textStyle={ComponentTextStyle.Bold}>{noteLeft}</ComponentXXS>
          </CardBottomSectionNoteLeftStyles>
        )}
        {(noteRight || noteRightIcon) && (
          <CardBottomSectionNoteRightStyles>
            {noteRightIcon}
            <ComponentXXS color={COLORS.neutral_500} textStyle={ComponentTextStyle.Bold}>{noteRight}</ComponentXXS>
          </CardBottomSectionNoteRightStyles>
        )}
      </CardBottomSectionNotesStyles>}

      {(authorName || logo) && <CardBottomSectionAuthorStyles disabled={disabled} data-testid={'card-bottomSection-author'}>
        <ComponentXXS>{authorName}</ComponentXXS>
        {logo}
      </CardBottomSectionAuthorStyles>}

      {actions && <CardBottomSectionButtonRowContainerStyles>
        {actions.map((x, index) => (
          <IconButton ref={elRefs[index]}
                      key={index}
                      disabled={disabled}
                      variant="secondary"
                      shape="circular"
                      action={(e) => {
                        x.onClick();
                      }}>
            {x.icon}
          </IconButton>
        ))}
      </CardBottomSectionButtonRowContainerStyles>}
    </CardBottomSectionContainer>
  );
});

export default CardBottomSection;
